import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import axios from 'axios';
import { sendReport } from '../../functions/sendReport';
import { fetchThemeData } from '../../admin/themeSlice';
import { toSentenceCase } from '../../utils/sentenceCase';
import '../photobooth/photobooth.css';
import '../avatar/avatar.css';
import './gallery.css';

const API           = process.env.REACT_APP_PHOTOBOOTH_API_URL;
const S3            = process.env.REACT_APP_S3_PATH || '';
const GALLERY_LIMIT = 6;

// Must mirror the backend's EMOJI_SCORES (app.py) — display order + weights.
const REACTION_EMOJIS = ['❤️', '🔥', '😍', '👏', '👍'];

const parseObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const DAY_KEYS = Array.from({ length: 9 }, (_, i) => `day${i + 1}`);

// Mirrors old welcome.php's "welcome.php?find=dayN" swatches — plain color
// circles the player clicks to filter the gallery, "All" plus one per day.
const ALL_KEY = 'ALL';

/** Standalone gallery page — day-color filters on top, image grid + reactions below. */
export default function Gallery() {
  const { user }            = useSelector(state => state.auth);
  const { data: themeData } = useSelector(state => state.theme);
  const navigate             = useNavigate();
  const dispatch              = useDispatch();

  const [gallery, setGallery] = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [galleryCategory, setGalleryCategory] = useState(ALL_KEY);
  const [floatEmojis, setFloatEmojis] = useState([]); // [{ id, emoji, x, y, dx, delay }]
  const floatIdRef      = useRef(0);
  const likeInFlightRef = useRef(new Set());

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  const dayToggles     = parseObject(themeData?.day_toggles);
  const dayLabels      = parseObject(themeData?.day_labels);
  const dayColors      = parseObject(themeData?.day_colors);
  const characterTheme = parseObject(themeData?.character_theme);
  const navratriEnabled = dayToggles.navratri_ai !== false;

  // Only days the admin has turned on show up here — mirrors the Avatar page.
  const categoryOptions = navratriEnabled
    ? DAY_KEYS.filter(k => dayToggles[k] !== false && Array.isArray(characterTheme[k]) && characterTheme[k].length > 0)
    : [];

  const getDayLabel = (dayKey) => dayLabels[dayKey] || `Day ${dayKey.replace('day', '')}`;
  const getDayColor = (dayKey) => dayColors[dayKey] || '#cccccc';

  const authHeader = () => ({
    Authorization: `Bearer ${user?.sessionId}&${user?.organizationId}`,
  });

  const fetchGallery = async (p, cat = galleryCategory) => {
    try {
      const res = await axios.post(
        `${API}/api/photobooth/gallery`,
        { userId: user.userId, page: p, limit: GALLERY_LIMIT, category: cat && cat !== ALL_KEY ? cat : undefined },
        { headers: authHeader() },
      );
      setGallery(res.data.items || []);
      setTotal(res.data.total  || 0);
      setPage(p);
    } catch (err) {
      console.error('Gallery fetch failed', err);
    }
  };

  useEffect(() => {
    if (user?.sessionId) fetchGallery(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sessionId]);

  const handleGalleryCategoryChange = (cat) => {
    setGalleryCategory(cat);
    fetchGallery(0, cat);
  };

  const spawnBurst = (emoji) => {
    const ids = [];
    const particles = Array.from({ length: 8 }).map(() => {
      const id = floatIdRef.current++;
      ids.push(id);
      return {
        id,
        emoji,
        x: Math.random() * window.innerWidth,
        y: window.innerHeight,
        dx: (Math.random() - 0.5) * 120,
        delay: Math.random() * 300,
      };
    });
    setFloatEmojis(prev => [...prev, ...particles]);
    setTimeout(() => {
      setFloatEmojis(prev => prev.filter(p => !ids.includes(p.id)));
    }, 2600);
  };

  const handleLike = async (post, emoji) => {
    // Same emoji already picked — nothing to change.
    if (post.myReaction === emoji) return;
    if (likeInFlightRef.current.has(post.id)) return;
    const isFirstReaction = !post.liked;
    spawnBurst(emoji);
    likeInFlightRef.current.add(post.id);
    try {
      const res = await axios.post(
        `${API}/api/photobooth/like`,
        { userId: user.userId, postId: post.id, emoji },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } },
      );
      if (res.data.success) {
        const reactedEmoji = res.data.emoji || emoji;
        const reactorName  = user.name || user.email || 'You';
        setGallery(prev =>
          prev.map(p => {
            if (p.id !== post.id) return p;
            const prevReaction = p.myReaction;
            const nextCounts = { ...(p.reactionCounts || {}) };
            const nextUsers  = { ...(p.reactionUsers  || {}) };

            // Switching reactions moves the tally from the old emoji to the
            // new one instead of double-counting this user under both.
            if (prevReaction && prevReaction !== reactedEmoji) {
              if (nextCounts[prevReaction]) {
                nextCounts[prevReaction] = Math.max(0, nextCounts[prevReaction] - 1);
                if (nextCounts[prevReaction] === 0) delete nextCounts[prevReaction];
              }
              if (nextUsers[prevReaction]) {
                nextUsers[prevReaction] = nextUsers[prevReaction].filter(n => n !== reactorName);
                if (nextUsers[prevReaction].length === 0) delete nextUsers[prevReaction];
              }
            }
            if (prevReaction !== reactedEmoji) {
              nextCounts[reactedEmoji] = (nextCounts[reactedEmoji] || 0) + 1;
              nextUsers[reactedEmoji]  = [...(nextUsers[reactedEmoji] || []), reactorName];
            }

            return {
              ...p,
              liked:      true,
              myReaction: reactedEmoji,
              likes:      res.data.likes,
              reactionCounts: nextCounts,
              reactionUsers:  nextUsers,
            };
          }),
        );

        // The "likes" total only changes on a first-time reaction, not when
        // switching which emoji was picked — only report points then.
        if (isFirstReaction && post.likesid) {
          try {
            await sendReport({
              role: user.role,
              token: user.token,
              reportId: post.likesid,
              points: res.data.likes,
              time: "00:00",
            });
          } catch (reportErr) {
            console.error('sendReport (like point) failed', reportErr);
          }
        }
      }
    } catch {
    } finally {
      likeInFlightRef.current.delete(post.id);
    }
  };

  // Frontend-only (no backend change) — best-effort blob download/share.
  // If the S3 bucket doesn't send CORS headers for GET, the fetch below
  // throws and this falls back to opening the image in a new tab, where
  // the user can save it manually (long-press / right-click save).
  const handleDownload = async (post) => {
    if (!post.imageUrl) return;
    try {
      const resp = await fetch(post.imageUrl);
      const blob = await resp.blob();
      const file = new File([blob], 'utsav-avatar.jpg', { type: blob.type || 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Utsav Avatar' });
        return;
      }
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'utsav-avatar.jpg';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      window.open(post.imageUrl, '_blank', 'noreferrer');
    }
  };

  const handleDelete = async (post) => {
    const confirm = await Swal.fire({
      title: 'Delete this photo?',
      text: 'This will remove it from the gallery.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#e11d48',
    });
    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.post(
        `${API}/api/photobooth/delete`,
        { userId: user.userId, postId: post.id },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } },
      );
      if (res.data.success) {
        setGallery(prev => prev.filter(p => p.id !== post.id));
        setTotal(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Could not delete this photo.', 'error');
    }
  };

  if (!themeData) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader"></div>
      </div>
    );
  }

  const isMobile = window.innerWidth <= 768;
  const bgImg    = isMobile ? themeData.background_mob : themeData.background_desk;
  const bgStyle  = bgImg
    ? {
        backgroundImage:      `url("${S3}${bgImg}")`,
        backgroundSize:       '100% 100%',
        backgroundRepeat:     'no-repeat',
        backgroundAttachment: 'fixed',
      }
    : {};

  const btnStyle = {
    background: themeData?.button_background
      ? themeData.button_background
      : `linear-gradient(180deg, ${themeData?.button_color || '#fb9905'}, ${themeData?.button_color || '#f97316'})`,
    color: themeData?.button_text_color ? themeData.button_text_color : '#fff',
  };

  // Admin's Gallery-tab "Gallery heading" (gallery_heading) — no static
  // fallback: if it's empty, show nothing rather than a placeholder.
  const galleryHeadingText = toSentenceCase(themeData?.gallery_heading);

  const totalPages = Math.ceil(total / GALLERY_LIMIT) || 0;
  // A capped, current-page-centered window of dot indicators — jumping
  // straight to a nearby page — rather than one dot per page, which would
  // sprawl once there are many pages.
  const maxDots = Math.min(totalPages, 5);
  let dotStart = Math.max(0, Math.min(page - Math.floor(maxDots / 2), totalPages - maxDots));
  const dotPages = Array.from({ length: maxDots }, (_, i) => dotStart + i);

  return (
    <div className="av-page gal-page" style={bgStyle}>
      <div className="av-nav-tab-row">
        <button type="button" className="av-nav-tab" style={btnStyle} onClick={() => navigate('/avatar')}>
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
            <path d="M4 11.5 12 4l8 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M6 10v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 20v-5h4v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Main
        </button>
      </div>

      <div className="gal-card">
        {themeData?.festiveGalleryImage ? (
          <img
            src={`${S3}${themeData.festiveGalleryImage}`}
            alt="Festive Gallery"
            className="gal-ribbon gal-ribbon-image"
          />
        ) : galleryHeadingText ? (
          <div className="gal-ribbon">
            <span aria-hidden="true">✤</span> {galleryHeadingText} <span aria-hidden="true">✤</span>
          </div>
        ) : null}

        {/* ── Day-color filters ────────────────────────────────────────── */}
        <div className="gal-filter-bar">
          <button
            type="button"
            className={`gal-filter-all${galleryCategory === ALL_KEY ? ' gal-filter-all-active' : ''}`}
            style={galleryCategory === ALL_KEY ? btnStyle : undefined}
            onClick={() => handleGalleryCategoryChange(ALL_KEY)}
          >
            <span className="gal-filter-all-swirl av-pill-rainbow" aria-hidden="true" />
            All
          </button>
          {categoryOptions.map(day => (
            <button
              type="button"
              key={day}
              className={`gal-filter-day${galleryCategory === day ? ' gal-filter-day-active' : ''}`}
              onClick={() => handleGalleryCategoryChange(day)}
            >
              <span className="gal-filter-day-dot" style={{ background: getDayColor(day) }} />
              <span className="gal-filter-day-label">{getDayLabel(day)}</span>
            </button>
          ))}
        </div>

        {/* ── Gallery grid ──────────────────────────────────────────────── */}
        {gallery.length === 0 ? (
          <p className="gal-empty">There is no post in this category</p>
        ) : (
          <div className="gal-grid">
            {gallery.map(item => {
              const isBlankName = !item.name || item.name.trim().toLowerCase() === "null null";
              const displayName = isBlankName ? item.email : item.name;

              return (
                <div key={item.id} className="gal-item">
                  <div className="gal-item-img-wrap">
                    <img src={item.imageUrl} alt={displayName} className="gal-item-img" />
                    {item.isOwner && (
                      <div className="gal-item-owner-actions">
                        <button
                          type="button"
                          className="gal-item-delete-btn"
                          onClick={() => handleDelete(item)}
                          title="Delete this photo"
                          aria-label="Delete this photo"
                        >
                          &times;
                        </button>
                        <button
                          type="button"
                          className="gal-item-download-btn"
                          onClick={() => handleDownload(item)}
                          title="Download this photo"
                          aria-label="Download this photo"
                        >
                          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" aria-hidden="true">
                            <path d="M12 4v11m0 0 4-4m-4 4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M5 18v1a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="gal-item-footer">
                    <div className="pb-reaction-row">
                      {REACTION_EMOJIS.map(emoji => {
                        const isMine = item.liked && item.myReaction === emoji;
                        const count  = item.reactionCounts?.[emoji] || 0;
                        const names  = item.reactionUsers?.[emoji] || [];
                        const tooltip = count > 0
                          ? `${emoji} (${count})\n${names.join('\n')}`
                          : emoji;
                        return (
                          <button
                            key={emoji}
                            className={`pb-reaction-btn ${isMine ? 'picked' : ''}`}
                            onClick={() => handleLike(item, emoji)}
                            title={tooltip}
                          >
                            <span className="pb-reaction-emoji">{emoji}</span>
                            {count > 0 && <span className="pb-reaction-count">{count}</span>}
                          </button>
                        );
                      })}
                    </div>
                    <span className="gal-item-name">{displayName}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="gal-pagination">
            <button
              type="button"
              className="gal-pagination-arrow"
              disabled={page === 0}
              onClick={() => fetchGallery(page - 1)}
              aria-label="Previous page"
            >
              ‹
            </button>
            {dotPages.map(p => (
              <button
                type="button"
                key={p}
                className={`gal-pagination-dot${p === page ? ' gal-pagination-dot-active' : ''}`}
                onClick={() => fetchGallery(p)}
                aria-label={`Page ${p + 1}`}
              />
            ))}
            <button
              type="button"
              className="gal-pagination-arrow"
              disabled={page >= totalPages - 1}
              onClick={() => fetchGallery(page + 1)}
              aria-label="Next page"
            >
              ›
            </button>
          </div>
        )}
      </div>

      {/* ── Floating reaction emojis ──────────────────────────────── */}
      <div className="pb-emoji-layer">
        {floatEmojis.map(f => (
          <span
            key={f.id}
            className="pb-float-emoji"
            style={{
              left: `${f.x}px`,
              top: `${f.y}px`,
              '--dx': `${f.dx}px`,
              animationDelay: `${f.delay}ms`,
            }}
          >
            {f.emoji}
          </span>
        ))}
      </div>
    </div>
  );
}
